import React, { type DetailedHTMLProps, type HTMLAttributes, type FocusEventHandler, type FormEvent, useRef, forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { SupText } from "../../../components/UI/Text";
import { useAutoFocus } from "../../../hooks/useAutoFocus";
import { useMergedRef } from "../../../hooks/useMergedRef";
import type { CommonColors } from "../../../types/colors";
import { useControlledState } from "../../../hooks/useControlledState";

export interface TextareaProps {
  id?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  color?: CommonColors;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  fluid?: boolean;
  cols?: number;
  rows?: number;
  limit?: number;
  textareaProps?: Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
    "id" | "name" | "value" | "defaultValue" | "disabled" | "placeholder" | "autoFocus" | "ref" | "cols" | "rows"
  > & { pattern?: string };
  onChange?: (value?: string | undefined) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
}

const borderColors = {
  primary: "border-0.5 border-neutral-500 enabled:hover:border-neutral-700 enabled:focus:border-neutral-500 enabled:active:border-neutral-800",
  success: "border-0.5 border-success-900",
  info: "border-0.5 border-info-800",
  error: "border-0.5 border-error-925",
  warning: "border-0.5 border-warning-900",
} as const;

export const SupTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, value, fluid, color = "primary", autoFocus, textareaProps, rows = 5, limit, defaultValue, onChange, ...restProps },
  ref
) {
  const [inputValue, setInputValue] = useControlledState<string | undefined>({ value, defaultValue, onStateChange: onChange });
  const elRef = useRef<HTMLTextAreaElement>(null);
  // @ts-ignore
  const textareaRef = useMergedRef(ref, elRef);

  useAutoFocus(elRef.current, { enabled: autoFocus });

  function handleChange(e: FormEvent<HTMLTextAreaElement>) {
    const { value } = e.currentTarget;

    if (limit && value && value.length > limit) return;

    setInputValue(value);
  }

  return (
    <div className={twMerge("sup-textarea inline-flex flex-col gap-1", fluid ? "w-full" : "", className)}>
      <SupText
        ref={textareaRef}
        as="textarea"
        {...restProps}
        {...textareaProps}
        value={inputValue}
        rows={rows}
        variant="subtitle1"
        className={twMerge(
          "w-full px-4 py-5 outline-0 transition-colors duration-200 placeholder:text-mono placeholder:text-xs placeholder:leading-4 placeholder:text-neutral-600 border-0.5 bg-neutral-50 enabled:hover:bg-neutral-75 enabled:focus:bg-neutral-50 enabled:active:hover:bg-neutral-75 disabled:bg-neutral-50",
          borderColors[color],
          textareaProps?.className
        )}
        onChange={handleChange}
      />
      {limit && (
        <SupText as="div" variant="caption">
          {inputValue?.length ?? 0}/{limit}
        </SupText>
      )}
    </div>
  );
});
